import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import api from "../../services/api";
import {
    FormControl as MuiFormControl,
    InputLabel as MuiInputLabel,
    MenuItem as MuiMenuItem,
    Select as MuiSelect,
    Stack
} from "@mui/material";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { Bot, Columns3 } from "lucide-react";
import ChatiaDialog from "../ChatiaDialog";

const useStyles = makeStyles(theme => ({
    root: { display: "flex", flexWrap: "wrap" },
    btnWrapper: { position: "relative" },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12
    },
    content: {
        gap: 12,
        display: "flex",
        flexDirection: "column",
    },
    infoCard: {
        background: theme.mode === "dark" ? "#16191f" : "#ffffff",
        color: theme.mode === "dark" ? "#e9edef" : "#111b21",
        border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
        borderRadius: 12,
        padding: 16,
        boxShadow: "0 10px 28px rgba(0, 0, 0, 0.16)",
    },
    cardHeader: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
        color: theme.mode === "dark" ? "#e9edef" : "#111b21",
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: 0.1,
    },
    cardIcon: {
        width: 32,
        height: 32,
        borderRadius: 10,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.04)",
        border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
        color: theme.palette.primary.main,
        "& svg": {
            color: theme.palette.primary.main,
            stroke: theme.palette.primary.main,
        },
    },
}));

const FlowBuilderKanbanPhaseModal = ({ open, onSave, data, onUpdate, close }) => {
    const classes = useStyles();
    const theme = useTheme();
    const isMounted = useRef(true);
    const [activeModal, setActiveModal] = useState(false);
    const [phases, setPhases] = useState([]);
    const [selectedPhase, setSelectedPhase] = useState("");
    const [aiTimeoutSeconds, setAiTimeoutSeconds] = useState(0);

    useEffect(() => {
        if (open === "edit") {
            (async () => {
                try {
                    const { data: tagsData } = await api.get("/tags", { params: { kanban: 1 } });
                    const kanbanTags = (tagsData.tags || tagsData || []).filter(t => Number(t.kanban) === 1);
                    setPhases(kanbanTags);
                    const phaseId = data?.data?.id || data?.id;
                    if (phaseId) setSelectedPhase(phaseId);
                    setAiTimeoutSeconds(data?.data?.aiTimeoutSeconds || data?.aiTimeoutSeconds || 0);
                    setActiveModal(true);
                } catch (error) {
                    console.log(error);
                }
            })();
        } else if (open === "create") {
            (async () => {
                try {
                    const { data: tagsData } = await api.get("/tags", { params: { kanban: 1 } });
                    const kanbanTags = (tagsData.tags || tagsData || []).filter(t => Number(t.kanban) === 1);
                    setPhases(kanbanTags);
                    setSelectedPhase("");
                    setAiTimeoutSeconds(0);
                    setActiveModal(true);
                } catch (error) {
                    console.log(error);
                }
            })();
        }
        return () => { isMounted.current = false; };
    }, [open]);

    const handleClose = () => {
        close(null);
        setActiveModal(false);
    };

    const handleSave = () => {
        if (!selectedPhase) {
            return toast.error("Selecione uma fase do Kanban");
        }
        const phase = phases.find(p => p.id === selectedPhase);
        if (open === "edit") {
            onUpdate({ ...data, data: { id: phase.id, name: phase.name, aiTimeoutSeconds: parseInt(aiTimeoutSeconds) || 0 } });
        } else {
            onSave({ data: { id: phase.id, name: phase.name, aiTimeoutSeconds: parseInt(aiTimeoutSeconds) || 0 } });
        }
        handleClose();
    };

    const title = open === "create" ? "Adicionar Fase Kanban" : "Editar Fase Kanban";

    const selectFieldSx = {
        mt: "8px",
        "& .MuiOutlinedInput-root": {
            borderRadius: "12px !important",
            minHeight: 40,
            color: theme.mode === "dark" ? "#e9edef" : "#111b21",
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: "12px !important",
            borderColor: theme.mode === "dark" ? "rgba(255, 255, 255, 0.18)" : "rgba(15, 23, 42, 0.18)",
        },
        "& .MuiInputBase-root": {
            borderRadius: "12px !important",
        },
        "& .MuiInputLabel-root": {
            backgroundColor: theme.mode === "dark" ? "#16191f" : "#ffffff",
            color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
            px: "4px",
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
        },
        "& .MuiSvgIcon-root": {
            color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
        },
    };

    const selectMenuProps = {
        PaperProps: {
            sx: {
                mt: 1,
                borderRadius: "14px",
                boxShadow: "0 10px 36px rgba(0,0,0,0.14)",
                border: "none",
                backgroundImage: "none",
                overflow: "hidden",
                "& .MuiMenu-list": {
                    padding: "6px",
                },
                "& .MuiMenuItem-root": {
                    borderRadius: "8px",
                    padding: "9px 12px",
                    marginBottom: "4px",
                    fontSize: 14,
                    fontWeight: 500,
                    "&.Mui-selected": {
                        background: "rgba(6, 207, 156, 0.10)",
                    },
                    "&.Mui-selected:hover": {
                        background: "rgba(6, 207, 156, 0.16)",
                    },
                },
            },
        },
    };

    const CardTitle = ({ icon: Icon, children }) => (
        <div className={classes.cardHeader}>
            <span className={classes.cardIcon}>
                <Icon size={16} />
            </span>
            <span>{children}</span>
        </div>
    );

    return (
        <div className={classes.root}>
            <ChatiaDialog
                open={activeModal}
                onClose={handleClose}
                title={title}
                maxWidth="md"
                contentDividers={false}
                onSave={handleSave}
                saveType="button"
                saveText={open === "create" ? "Adicionar" : "Salvar"}
            >
                <Stack className={classes.content}>
                    <Stack className={classes.infoCard}>
                        <CardTitle icon={Columns3}>{title}</CardTitle>
                        <MuiFormControl variant="outlined" fullWidth size="small" sx={selectFieldSx}>
                            <MuiInputLabel id="kanban-phase-selection-label">
                                Selecione uma fase...
                            </MuiInputLabel>
                            <MuiSelect
                                labelId="kanban-phase-selection-label"
                                id="kanbanPhase"
                                value={selectedPhase}
                                onChange={(e) => setSelectedPhase(e.target.value)}
                                label="Selecione uma fase..."
                                MenuProps={selectMenuProps}
                            >
                                {phases.map((phase) => (
                                    <MuiMenuItem key={phase.id} value={phase.id}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <div style={{
                                                width: 12, height: 12, borderRadius: "50%",
                                                backgroundColor: phase.color || "#ccc"
                                            }} />
                                            {phase.name}
                                        </div>
                                    </MuiMenuItem>
                                ))}
                            </MuiSelect>
                        </MuiFormControl>
                    </Stack>

                    <Stack className={classes.infoCard}>
                        <CardTitle icon={Bot}>
                            <Typography style={{ fontSize: "14px", fontWeight: "bold", color: theme.mode === "dark" ? "#e9edef" : "#111b21" }}>
                                Saída IA (tempo para IA assumir)
                            </Typography>
                        </CardTitle>
                        <TextField
                            label="Tempo em segundos (0 = desativado)"
                            type="number"
                            variant="outlined"
                            margin="dense"
                            value={aiTimeoutSeconds}
                            onChange={e => setAiTimeoutSeconds(e.target.value)}
                            fullWidth
                            InputProps={{ inputProps: { min: 0 } }}
                            helperText={aiTimeoutSeconds > 0 ? `Se o cliente não responder em ${aiTimeoutSeconds}s, a IA assume o atendimento (saída roxa)` : ""}
                        />
                    </Stack>
                </Stack>
            </ChatiaDialog>
        </div>
    );
};

export default FlowBuilderKanbanPhaseModal;
